import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { GENZ_SERIES } from '../components/genz/genzContent';
import { LessonMediaButtons } from '../components/genz/LessonMediaButtons';
import { getDirectUrl, getMediaUrl } from '../utils/mediaUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LessonMedia = ({ lesson, videoRef }) =>
  lesson.video_url ? (
    <div id="watch" className="bg-black rounded overflow-hidden shadow-2xl">
      <video
        ref={videoRef}
        controls
        playsInline
        preload="metadata"
        poster={getDirectUrl(lesson.thumbnail_url)}
        src={getMediaUrl(lesson.video_url)}
        className="w-full aspect-video bg-black"
        data-testid="lesson-video-player"
      />
    </div>
  ) : (
    <img src={getDirectUrl(lesson.thumbnail_url)} alt={lesson.title} className="w-full max-h-[560px] object-contain rounded shadow-2xl bg-black/20" data-testid="lesson-page-thumbnail" />
  );

const LessonText = ({ lesson }) => (
  <div className="max-w-3xl">
    {lesson.thesis && (
      <div className="mb-8 border-l-4 border-brand-accent pl-5" data-testid="lesson-thesis">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-brand-accent mb-2">Thesis</p>
        <p className="font-heading text-xl md:text-2xl text-brand-primary leading-snug">{lesson.thesis}</p>
      </div>
    )}
    {lesson.references && (
      <div className="mb-8" data-testid="lesson-references">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-brand-accent mb-2 flex items-center gap-2"><BookOpen className="h-4 w-4" /> Scripture References</p>
        <p className="font-body text-base text-slate-700">{lesson.references}</p>
      </div>
    )}
    <div className="space-y-5 font-body text-base md:text-lg leading-relaxed text-slate-700" data-testid="lesson-overview">
      {(lesson.overview || []).map((p, i) => <p key={i}>{p}</p>)}
    </div>
  </div>
);

export default function GenZLesson() {
  const { slug } = useParams();
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    setLesson(null);
    axios.get(`${API}/genz/lessons/${slug}`).then(({ data }) => setLesson(data)).catch(() => setError(true));
  }, [slug]);

  const watch = () => {
    videoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    videoRef.current?.play().catch(() => {});
  };

  return (
    <div className="min-h-screen bg-background-primary">
      <Navbar />
      {error && <p className="text-center py-32 font-body text-slate-600" data-testid="lesson-not-found">Lesson not found.</p>}
      {!lesson && !error && <p className="text-center py-32 font-body text-slate-600">Loading...</p>}
      {lesson && (
        <>
          <SEO title={`Lesson ${lesson.order}: ${lesson.title} | ${GENZ_SERIES.title}`} description={lesson.short_description} url={`https://restoremyword.net/gen-z/${lesson.slug}`} image={lesson.thumbnail_url} />
          <section className="bg-brand-primary py-12 md:py-16" data-testid="lesson-page">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <Link to="/gen-z" className="inline-flex items-center gap-2 text-sm font-body text-white/70 hover:text-brand-accent mb-6 transition-colors duration-300" data-testid="lesson-back-link">
                <ArrowLeft className="h-4 w-4" /> {GENZ_SERIES.title}
              </Link>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                <div className="lg:col-span-8"><LessonMedia lesson={lesson} videoRef={videoRef} /></div>
                <div className="lg:col-span-4">
                  <p className="text-xs font-semibold tracking-[0.3em] uppercase text-brand-accent mb-3">Lesson {lesson.order}</p>
                  <h1 className="font-heading text-3xl md:text-4xl font-semibold text-white leading-tight mb-4" data-testid="lesson-page-title">{lesson.title}</h1>
                  <p className="font-body text-sm text-white/75 leading-relaxed mb-6">{lesson.short_description}</p>
                  <LessonMediaButtons lesson={lesson} onWatch={watch} />
                </div>
              </div>
            </div>
          </section>
          <section className="py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-6 md:px-12"><LessonText lesson={lesson} /></div>
          </section>
        </>
      )}
      <Footer />
    </div>
  );
}
